import { Injectable, inject } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { CustomerService, UserAddress } from './customer.service';
import { ClaimsService } from './claims.service';
import { AddressNumberService } from './address-number.service';
import { UserRegistrationData } from '@app/shared/models/user.model';
import { TranslateService } from '@ngx-translate/core';
import { getCountries } from '@app/shared/components/form-control/search-select/data/countries';
import { getStatesForCountry } from '@app/shared/components/form-control/search-select/data/states';

type AddressType = 'I' | 'D' | 'M';

// ===== PRIORITY CONFIGURATION =====
// Change the active profile to switch data source priorities across all methods
// 'registration-first': UserRegistration -> Customer -> OIDC (new requirement)
// 'customer-first': Customer -> UserRegistration -> OIDC (previous requirement)
type PriorityProfile = 'registration-first' | 'customer-first';

const PRIORITY_PROFILES: Record<PriorityProfile, ('customer' | 'registration' | 'oidc')[]> = {
	'registration-first': ['registration', 'customer', 'oidc'],
	'customer-first': ['customer', 'registration', 'oidc'],
};

// **CHANGE THIS to switch between profiles**
const ACTIVE_PRIORITY_PROFILE: PriorityProfile = 'registration-first';

@Injectable({
	providedIn: 'root',
})
export class UserService {
	private readonly customerService = inject(CustomerService);
	private readonly claimsService = inject(ClaimsService);
	private readonly oauthService = inject(OAuthService);
	private readonly translateService = inject(TranslateService);
	private readonly addressNumberService = inject(AddressNumberService);

	private get oidcClaims(): Record<string, string> | null {
		return (this.oauthService.getIdentityClaims() as Record<string, string>) ?? null;
	}

	// ===== PRIORITY RESOLUTION UTILITIES =====
	/**
	 * Resolves a value based on the current priority profile.
	 * Returns the first non-null/non-empty value from the priority chain.
	 * @param resolvers Object mapping data source to resolver function
	 * @returns The resolved value or undefined if all sources return falsy values
	 */
	private resolvePriority<T>(resolvers: {
		customer?: () => T | null | undefined;
		registration?: () => T | null | undefined;
		oidc?: () => T | null | undefined;
	}): T | undefined {
		const profile = PRIORITY_PROFILES[ACTIVE_PRIORITY_PROFILE];

		for (const source of profile) {
			const resolver = resolvers[source as keyof typeof resolvers];
			if (resolver) {
				const value = resolver();
				if (value) return value;
			}
		}

		return undefined;
	}

	/**
	 * Resolves a string value with fallback, based on priority profile.
	 * @param resolvers Object mapping data source to resolver function
	 * @param fallback Default value if no priority sources provide a value
	 * @returns The resolved value or fallback
	 */
	private resolvePriorityString(
		resolvers: {
			customer?: () => string | null | undefined;
			registration?: () => string | null | undefined;
			oidc?: () => string | null | undefined;
		},
		fallback = '',
	): string {
		const value = this.resolvePriority(resolvers);
		return value ?? fallback;
	}

	/** Emits every time customer data is refreshed (e.g. address number switch). */
	public readonly customerData$ = this.customerService.customerData$;

	// Cached data properties
	private get customer() {
		return this.customerService.getCurrentCustomerValue();
	}

	private get userRegistration() {
		return this.claimsService.getCurrentUserRegistrationDataValue();
	}

	/**
	 * Returns the first address matching the given type. If no address of that
	 * type exists, falls back to the first address in the list regardless of type.
	 * @param type Address type: 'I' (invoice), 'D' (delivery), 'M' (main)
	 * @returns The first address object of the requested type, or null if no addresses exist
	 */
	private getAddressByType(type: AddressType): UserAddress | null {
		if (!this.customer?.usersAddresses) return null;
		const selectedAN = this.addressNumberService.selectedAddressNumber;
		const pool = selectedAN
			? (this.customer.usersAddresses.filter(
					(ua) => !ua['addressNumber'] || ua['addressNumber'] === selectedAN,
				) as UserAddress[])
			: this.customer.usersAddresses;
		return pool.find((userAddress) => userAddress.atype === type) ?? pool[0] ?? null;
	}

	/**
	 * Returns all addresses matching the given type.
	 * Unlike {@link getAddressByType}, this method returns every match without
	 * any fallback, so the result may be an empty array.
	 * @param type Address type: 'I' (invoice), 'D' (delivery), 'M' (main)
	 * @returns Array of address objects of the requested type
	 */
	getAddressesByType(type: AddressType): UserAddress[] {
		if (!this.customer?.usersAddresses) return [];
		const selectedAN = this.addressNumberService.selectedAddressNumber;
		const pool = selectedAN
			? this.customer.usersAddresses.filter((ua) => !ua['addressNumber'] || ua['addressNumber'] === selectedAN)
			: this.customer.usersAddresses;
		return pool.filter((userAddress: UserAddress) => userAddress.atype === type);
	}

	// ===== CENTRALIZED PERSONAL DATA ACCESS METHODS =====

	/**
	 * Get user's full name respecting the configured priority profile
	 * @returns The user's full name or 'User' if not available
	 */
	getUserFullName(): string {
		return this.resolvePriorityString(
			{
				customer: () =>
					this.customer?.name && this.customer?.surname
						? `${this.customer.name} ${this.customer.surname}`
						: (this.customer?.name ?? undefined),
				registration: () =>
					this.userRegistration?.firstName && this.userRegistration?.lastName
						? `${this.userRegistration.firstName} ${this.userRegistration.lastName}`
						: (this.userRegistration?.firstName ?? undefined),
				oidc: () =>
					this.oidcClaims?.['given_name'] && this.oidcClaims?.['family_name']
						? `${this.oidcClaims['given_name']} ${this.oidcClaims['family_name']}`
						: (this.oidcClaims?.['name'] ?? undefined),
			},
			'User',
		);
	}

	/**
	 * Get user's email respecting the configured priority profile
	 * Note: Email typically prioritizes UserRegistration/OIDC over customer data
	 * @returns The user's email address or 'User' if not available
	 */
	getUserEmail(): string {
		return this.resolvePriorityString(
			{
				customer: () => this.customer?.email ?? undefined,
				registration: () => this.userRegistration?.email ?? undefined,
				oidc: () => this.oidcClaims?.['email'] ?? undefined,
			},
			'User',
		);
	}

	/**
	 * Get user's first name respecting the configured priority profile
	 * @returns The user's first name or 'User' as fallback
	 */
	getUserFirstName(): string {
		return this.resolvePriorityString(
			{
				customer: () => this.customer?.name ?? undefined,
				registration: () => this.userRegistration?.firstName ?? undefined,
				oidc: () => this.oidcClaims?.['given_name'] ?? undefined,
			},
			'User',
		);
	}

	/**
	 * Get user's last name respecting the configured priority profile
	 * @returns The user's last name or empty string as fallback
	 */
	getUserLastName(): string {
		return this.resolvePriorityString(
			{
				customer: () => this.customer?.surname ?? undefined,
				registration: () => this.userRegistration?.lastName ?? undefined,
				oidc: () => this.oidcClaims?.['family_name'] ?? undefined,
			},
			'',
		);
	}

	/**
	 * Get formatted address string
	 * @returns Formatted address or empty string if no address data available
	 */
	getFormattedAddress(): string {
		const street = this.getUserStreet();
		const streetNumber = this.getUserStreetNumber();
		const postalCode = this.getUserPostalCode();
		const city = this.getUserCity();
		const countryCode = this.getUserCountryCode();

		if (!street && !city) return '';

		const streetLine = streetNumber ? `${street} ${streetNumber}` : street;
		return `${streetLine}, ${postalCode} ${city}, ${countryCode}`;
	}

	/**
	 * Check if we have any user data available
	 * @returns True if we have customer or user registration data
	 */
	hasUserData(): boolean {
		return Boolean(this.customer || this.userRegistration || this.oidcClaims?.['sub']);
	}

	/**
	 * Check if we have any customer data available
	 * @returns True if we have customer data
	 */
	hasCustomerData(): boolean {
		return Boolean(this.customer);
	}

	/**
	 * Force refresh of underlying data sources
	 * Call this after form submissions to ensure fresh data
	 */
	async refreshData(): Promise<void> {
		try {
			// Force refresh both data sources
			await Promise.all([
				this.customerService.refreshCustomerData().toPromise(),
				this.claimsService.refreshUserRegistrationData().toPromise(),
			]);
			console.log('UserService: Data refreshed successfully');
		} catch (error) {
			console.error('UserService: Error refreshing data:', error);
			throw error;
		}
	}

	/**
	 * Get user's company name from UserRegistration data
	 * @returns The user's company name or empty string if not available
	 */
	getCompanyName(): string {
		return this.userRegistration?.companyName || '';
	}

	/**
	 * Get user's initials respecting the configured priority profile
	 * @returns User's initials (up to 2 characters)
	 */
	getUserInitials(): string {
		const getInitialsFromFullName = (fullName: string): string => {
			const nameParts = fullName.split(' ').filter((part) => part.length > 0);
			if (nameParts.length >= 2) {
				return (nameParts[0].charAt(0) + nameParts[1].charAt(0)).toUpperCase();
			}
			return nameParts[0].charAt(0).toUpperCase();
		};

		return this.resolvePriorityString(
			{
				customer: () => {
					if (this.customer?.name && this.customer?.surname) {
						return getInitialsFromFullName(`${this.customer.name} ${this.customer.surname}`);
					}
					return this.customer?.name ? this.customer.name.charAt(0).toUpperCase() : undefined;
				},
				registration: () => {
					if (this.userRegistration?.firstName && this.userRegistration?.lastName) {
						return getInitialsFromFullName(`${this.userRegistration.firstName} ${this.userRegistration.lastName}`);
					}
					return this.userRegistration?.firstName ? this.userRegistration.firstName.charAt(0).toUpperCase() : undefined;
				},
				oidc: () => {
					if (this.oidcClaims?.['given_name'] && this.oidcClaims?.['family_name']) {
						return getInitialsFromFullName(`${this.oidcClaims['given_name']} ${this.oidcClaims['family_name']}`);
					}
					if (this.oidcClaims?.['given_name']) {
						return this.oidcClaims['given_name'].charAt(0).toUpperCase();
					}
					return this.oidcClaims?.['name'] ? this.oidcClaims['name'].charAt(0).toUpperCase() : undefined;
				},
			},
			'',
		);
	}

	// ===== CENTRALIZED ADDRESS DATA ACCESS METHODS =====

	/**
	 * General address field accessor with userRegistration fallback.
	 * Always prioritizes customer addresses over registration data.
	 * @param type Address type: 'I' (invoice), 'D' (delivery/shipping), 'M' (main)
	 * @param field Field name on the address object
	 */
	getAddressField(type: AddressType, field: string): string {
		// Always use customer address first (from getAddressByType)
		const address = this.getAddressByType(type);
		if (address?.[field]) return String(address[field]);

		// Fallback to registration data if customer address doesn't have the field
		const registrationFieldMap: Record<string, string> = {
			street: 'street',
			streetNumber: 'streetNumber',
			additionalAddressInfo: 'additionalAddressInfo',
			city: 'city',
			postalCode: 'zipCode',
			countryCode: 'countryCode',
			stateCode: 'stateCode',
		};

		const registrationField = registrationFieldMap[field];
		return registrationField
			? String((this.userRegistration as UserRegistrationData)?.[registrationField as keyof UserRegistrationData] ?? '')
			: '';
	}

	// --- Main (M) address convenience methods ---
	getUserStreet(): string {
		return this.getAddressField('M', 'street');
	}

	getUserStreetNumber(): string {
		return this.getAddressField('M', 'streetNumber');
	}

	getUserAdditionalAddressInfo(): string {
		const address = this.getAddressByType('M');
		if (address) {
			// Customer (SAP) address: secondary line is street2 only.
			// Do NOT fall back to registration data when street2 is empty.
			return address['street2'] ? String(address['street2']) : '';
		}
		// No customer address — use registration data.
		return String(this.userRegistration?.additionalAddressInfo ?? '');
	}

	getUserCity(): string {
		return this.getAddressField('M', 'city');
	}

	getUserPostalCode(): string {
		return this.getAddressField('M', 'postalCode');
	}

	getUserCountryCode(): string {
		return this.getAddressField('M', 'countryCode');
	}

	getUserStateCode(): string {
		return this.getAddressField('M', 'stateCode');
	}

	getUserAddressNumber(): string {
		return this.getAddressField('M', 'addressNumber');
	}

	getUserHasAddressNumber(): boolean {
		return !!this.getUserAddressNumber();
	}

	// ===== UTILITY METHODS =====

	/**
	 * Get the full country name from a country code, using translations if available
	 * @param countryCode The country code to translate
	 * @returns The translated country name or the original code if not found
	 */
	getCountryName(countryCode?: string): string {
		if (!countryCode) return '';

		const code = countryCode.toUpperCase();

		// Try to get translated name
		const translated = this.translateService.instant(`COUNTRIES.${code}`);
		if (translated && translated !== `COUNTRIES.${code}`) return translated;

		// Fallback to original displayText from COUNTRIES
		const country = getCountries().find((c) => String(c.value) === code);
		return country ? country.displayText : countryCode; // fallback to code itself
	}

	/**
	 * Get the full state name from a state code and country code,
	 * using translations if available
	 * @param stateCode The state code to translate
	 * @param countryCode The country code the state belongs to
	 * @returns The translated state name or the original code if not found
	 */
	getStateName(stateCode?: string, countryCode?: string): string {
		if (!stateCode || !countryCode) return '';

		const state = stateCode.toUpperCase();
		const country = countryCode.toUpperCase();

		// Try to get translated name
		const translated = this.translateService.instant(`STATES.${country}.${state}`);
		if (translated && translated !== `STATES.${country}.${state}`) {
			return translated;
		}

		// Fallback to static state list
		const states = getStatesForCountry(country);
		const found = states.find((s) => String(s.value) === state);

		return found ? found.displayText : stateCode;
	}

	getUserLocationLabel(): string {
		const countryCode = this.getUserCountryCode();
		if (!countryCode) {
			return this.translateService.instant('COMMON.LOADING');
		}

		const country = this.getCountryName(countryCode);
		const stateCode = this.getUserStateCode();

		if (!stateCode) {
			return country;
		}

		const state = this.getStateName(stateCode, countryCode);
		return state ? `${state}, ${country}` : country;
	}

	// ===== USER SETTINGS ACCESS METHODS =====

	/**
	 * Get a specific user setting by name
	 *
	 * Common setting examples:
	 * - 'delete_account_enabled': boolean flag to enable/disable delete account feature
	 * - 'change_password_enabled': boolean flag to enable/disable password change feature
	 * - 'theme': user's preferred theme ('dark', 'light', 'auto')
	 * - 'preferred_language': user's language preference ('en', 'de', 'fr', etc.)
	 * - 'notifications_enabled': boolean flag for email/push notifications
	 * - 'items_per_page': number of items to show per page in lists
	 * - 'timezone': user's timezone preference
	 * - 'date_format': preferred date format ('DD/MM/YYYY', 'MM/DD/YYYY', etc.)
	 *
	 * @param settingName The name of the setting to retrieve
	 * @returns The setting value or null if not found
	 */
	getUserSetting(settingName: string): string | null {
		if (!this.customer?.usersSettings) return null;

		const lowerName = settingName.toLowerCase();
		const setting = this.customer.usersSettings.find((s) => s.name?.toLowerCase() === lowerName);
		return setting ? setting.value : null;
	}

	/**
	 * Get all user settings as a key-value object
	 * @returns Object with setting names as keys and values as strings
	 */
	getAllUserSettings(): Record<string, string> {
		if (!this.customer?.usersSettings) return {};

		const settings: Record<string, string> = {};
		this.customer.usersSettings.forEach((setting) => {
			settings[setting.name] = setting.value;
		});
		return settings;
	}

	/**
	 * Get user setting as boolean value
	 * @param settingName The name of the setting to retrieve
	 * @param defaultValue Default value if setting doesn't exist or can't be parsed
	 * @returns Boolean value of the setting
	 */
	getUserSettingAsBoolean(settingName: string, defaultValue = false): boolean {
		const value = this.getUserSetting(settingName);
		if (value === null) return defaultValue;

		// Parse common boolean representations
		const lowerValue = value.toLowerCase();
		if (lowerValue === 'true' || lowerValue === '1' || lowerValue === 'yes') return true;
		if (lowerValue === 'false' || lowerValue === '0' || lowerValue === 'no') return false;

		return defaultValue;
	}

	/**
	 * Get user setting as number value
	 * @param settingName The name of the setting to retrieve
	 * @param defaultValue Default value if setting doesn't exist or can't be parsed
	 * @returns Number value of the setting
	 */
	getUserSettingAsNumber(settingName: string, defaultValue = 0): number {
		const value = this.getUserSetting(settingName);
		if (value === null) return defaultValue;

		const parsed = parseFloat(value);
		return isNaN(parsed) ? defaultValue : parsed;
	}
}
